import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Calculator, TrendingUp, Clock, Euro, BarChart3, 
  Target, ArrowRight, Info, CheckCircle, Building2,
  Users, PieChart, Calendar, Lightbulb, ArrowLeft
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SimulationROIPage = () => {
  // Form state
  const [formData, setFormData] = useState({
    activityType: '',
    collaborators: '',
    annualRevenue: '',
    hourlyRate: '',
    // Temps par tâche (heures/mois)
    adminTime: '',
    customerFollowUp: '',
    prospection: '',
    research: '',
    writing: '',
    contentCreation: '',
    otherTasks: '',
    // Niveau d'adoption
    adoptionLevel: '',
    // Coût personnalisé (optionnel)
    customCost: '',
    // Coût maintenance année 2-3
    maintenanceCost: '500',
  });

  const [showResults, setShowResults] = useState(false);

  // Configuration des niveaux d'adoption
  const adoptionLevels = {
    discovery: {
      label: 'Découverte',
      description: 'Usage ponctuel, premiers automatismes, appropriation partielle',
      gainRate: 0.08,
      defaultCost: 1500,
    },
    structuring: {
      label: 'Structuration',
      description: 'Usages réguliers, premiers processus cadrés, standardisation partielle',
      gainRate: 0.15,
      defaultCost: 3500,
    },
    deployment: {
      label: 'Déploiement opérationnel',
      description: 'Outils déployés dans plusieurs fonctions, pratiques intégrées au quotidien',
      gainRate: 0.22,
      defaultCost: 7500,
    },
    advanced: {
      label: 'Intégration avancée',
      description: 'IA intégrée à l\'organisation, automatisations matures, exploitation transversale',
      gainRate: 0.30,
      defaultCost: 12000,
    },
  };

  const activityTypes = [
    { value: 'tpe', label: 'TPE' },
    { value: 'pme', label: 'PME' },
    { value: 'formation', label: 'Organisme de formation' },
    { value: 'conseil', label: 'Cabinet de conseil' },
    { value: 'liberal', label: 'Profession libérale' },
    { value: 'commerce', label: 'Commerce' },
    { value: 'hospitality', label: 'Restauration / Hôtellerie' },
    { value: 'admin', label: 'Services administratifs' },
    { value: 'other', label: 'Autre' },
  ];

  // Handlers
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    // Allow only numbers and empty string
    const sanitized = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, [field]: sanitized }));
  };

  const handleDecimalChange = (field, value) => {
    // Allow numbers and one decimal point
    const sanitized = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setFormData(prev => ({ ...prev, [field]: sanitized }));
  };

  // Calculs
  const calculations = useMemo(() => {
    const hourlyRate = parseFloat(formData.hourlyRate) || 0;
    const annualRevenue = parseFloat(formData.annualRevenue) || 0;
    const level = adoptionLevels[formData.adoptionLevel];
    const maintenanceCost = parseFloat(formData.maintenanceCost) || 0;

    // Temps total mensuel (heures)
    const monthlyTotalHours = 
      (parseFloat(formData.adminTime) || 0) +
      (parseFloat(formData.customerFollowUp) || 0) +
      (parseFloat(formData.prospection) || 0) +
      (parseFloat(formData.research) || 0) +
      (parseFloat(formData.writing) || 0) +
      (parseFloat(formData.contentCreation) || 0) +
      (parseFloat(formData.otherTasks) || 0);

    if (!level || monthlyTotalHours === 0 || hourlyRate === 0) {
      return null;
    }

    const gainRate = level.gainRate;
    const implementationCost = formData.customCost 
      ? parseFloat(formData.customCost) 
      : level.defaultCost;

    // Gains de temps
    const monthlyTimeSaved = monthlyTotalHours * gainRate;
    const annualTimeSaved = monthlyTimeSaved * 12;
    const annualDaysSaved = annualTimeSaved / 7;

    // Gains économiques
    const annualEconomicGain = annualTimeSaved * hourlyRate;

    // Poids économique
    const economicWeight = annualRevenue > 0 
      ? (annualEconomicGain / annualRevenue) * 100 
      : 0;

    // ROI Année 1
    const netBenefitYear1 = annualEconomicGain - implementationCost;
    const roiYear1 = implementationCost > 0 
      ? ((annualEconomicGain - implementationCost) / implementationCost) * 100 
      : 0;

    // Projections années 2 et 3
    const gainYear2 = annualEconomicGain * 1.10;
    const gainYear3 = annualEconomicGain * 1.20;
    
    const netBenefitYear2 = gainYear2 - maintenanceCost;
    const netBenefitYear3 = gainYear3 - maintenanceCost;

    const roiYear2 = maintenanceCost > 0 
      ? ((gainYear2 - maintenanceCost) / maintenanceCost) * 100 
      : gainYear2 > 0 ? 100 : 0;
    const roiYear3 = maintenanceCost > 0 
      ? ((gainYear3 - maintenanceCost) / maintenanceCost) * 100 
      : gainYear3 > 0 ? 100 : 0;

    // Cumul sur 3 ans
    const totalGain3Years = annualEconomicGain + gainYear2 + gainYear3;
    const totalCost3Years = implementationCost + maintenanceCost * 2;
    const totalNetBenefit3Years = totalGain3Years - totalCost3Years;

    return {
      monthlyTotalHours,
      gainRate: gainRate * 100,
      monthlyTimeSaved,
      annualTimeSaved,
      annualDaysSaved,
      annualEconomicGain,
      economicWeight,
      implementationCost,
      netBenefitYear1,
      roiYear1,
      gainYear2,
      netBenefitYear2,
      roiYear2,
      gainYear3,
      netBenefitYear3,
      roiYear3,
      totalGain3Years,
      totalCost3Years,
      totalNetBenefit3Years,
      maintenanceCost,
    };
  }, [formData]);

  const formatNumber = (num, decimals = 0) => {
    return new Intl.NumberFormat('fr-FR', { 
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals 
    }).format(num);
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(num);
  };

  const isFormValid = () => {
    return (
      formData.activityType &&
      formData.collaborators &&
      formData.annualRevenue &&
      formData.hourlyRate &&
      formData.adoptionLevel &&
      (
        parseFloat(formData.adminTime) > 0 ||
        parseFloat(formData.customerFollowUp) > 0 ||
        parseFloat(formData.prospection) > 0 ||
        parseFloat(formData.research) > 0 ||
        parseFloat(formData.writing) > 0 ||
        parseFloat(formData.contentCreation) > 0 ||
        parseFloat(formData.otherTasks) > 0
      )
    );
  };

  const handleSimulate = () => {
    if (isFormValid()) {
      setShowResults(true);
      // Scroll to results
      setTimeout(() => {
        document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Component: Info Tooltip
  const InfoTooltip = ({ text }) => (
    <span className="group relative inline-block ml-1 cursor-help">
      <Info className="w-4 h-4 text-gray-400 inline" />
      <span className="invisible group-hover:visible absolute z-10 w-64 p-2 text-xs text-white bg-gray-800 rounded-lg -top-2 left-6 shadow-lg">
        {text}
      </span>
    </span>
  );

  // Component: Result Card
  const ResultCard = ({ icon: Icon, label, value, subValue, highlight, large }) => (
    <div className={`bg-white rounded-xl p-5 border ${highlight ? 'border-green-300 ring-2 ring-green-100' : 'border-gray-200'} ${large ? 'col-span-2' : ''}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${highlight ? 'bg-green-100' : 'bg-blue-50'}`}>
          <Icon className={`w-5 h-5 ${highlight ? 'text-green-600' : 'text-blue-700'}`} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">{label}</p>
          <p className={`font-bold ${large ? 'text-2xl' : 'text-xl'} ${highlight ? 'text-green-700' : 'text-gray-900'}`}>
            {value}
          </p>
          {subValue && (
            <p className="text-sm text-gray-500 mt-1">{subValue}</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#E9DFCF' }}>
      <Header />

      {/* Hero Section */}
      <section className="py-16 md:py-20 px-4" style={{ backgroundColor: '#1C2E4A' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6">
            <Calculator className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Simulation du retour sur investissement de l'IA dans votre organisation
          </h1>
          <p className="text-lg md:text-xl text-blue-100 mb-6 max-w-3xl mx-auto">
            Projetez les gains de temps, les économies potentielles et l'impact économique d'une démarche d'intégration de l'intelligence artificielle dans votre structure.
          </p>
          <p className="text-blue-200 max-w-2xl mx-auto mb-8">
            Cet outil vous permet d'estimer de manière indicative les bénéfices d'une transformation IA adaptée à votre activité, en fonction de vos données et hypothèses personnalisées.
          </p>
          <a href="#calculator">
            <Button
              className="text-white font-semibold py-4 px-8 rounded-full text-lg"
              style={{ backgroundColor: '#D8C9B3', color: '#1C2E4A' }}
            >
              Lancer la simulation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Bloc explicatif */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              À quoi sert cet outil ?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Cette simulation vous aide à évaluer l'intérêt économique d'une démarche d'intégration de l'IA dans votre organisation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Clock,
                title: 'Temps mobilisé',
                description: 'Identifiez les heures consacrées aux tâches à faible valeur ajoutée susceptibles d\'être optimisées.',
              },
              {
                icon: TrendingUp,
                title: 'Gains de temps',
                description: 'Estimez le temps potentiellement économisé grâce à l\'automatisation et l\'assistance IA.',
              },
              {
                icon: Euro,
                title: 'Valorisation économique',
                description: 'Traduisez ces gains de temps en équivalent économique selon votre taux horaire.',
              },
              {
                icon: PieChart,
                title: 'Poids dans votre activité',
                description: 'Mesurez l\'impact des gains estimés par rapport à votre chiffre d\'affaires.',
              },
              {
                icon: Target,
                title: 'Retour sur investissement',
                description: 'Calculez le ROI potentiel dès la première année d\'implémentation.',
              },
              {
                icon: Calendar,
                title: 'Projection pluriannuelle',
                description: 'Visualisez l\'évolution des bénéfices sur les trois premières années.',
              },
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#1C2E4A' }}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculateur */}
      <section id="calculator" className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Paramétrez votre simulation
            </h2>
            <p className="text-gray-600">
              Renseignez les informations relatives à votre structure pour obtenir une estimation personnalisée.
            </p>
          </div>

          <div className="space-y-8">
            {/* Section A: Informations générales */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#1C2E4A' }}>
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Informations générales</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-700 font-medium mb-2 block">
                    Type d'activité
                  </Label>
                  <Select value={formData.activityType} onValueChange={(v) => handleChange('activityType', v)}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Sélectionnez votre activité" />
                    </SelectTrigger>
                    <SelectContent>
                      {activityTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-700 font-medium mb-2 block">
                    Nombre de collaborateurs
                  </Label>
                  <Input
                    type="text"
                    value={formData.collaborators}
                    onChange={(e) => handleNumberChange('collaborators', e.target.value)}
                    placeholder="Ex: 12"
                    className="bg-white"
                  />
                </div>

                <div>
                  <Label className="text-gray-700 font-medium mb-2 block">
                    Chiffre d'affaires annuel HT (€)
                  </Label>
                  <Input
                    type="text"
                    value={formData.annualRevenue}
                    onChange={(e) => handleNumberChange('annualRevenue', e.target.value)}
                    placeholder="Ex: 500000"
                    className="bg-white"
                  />
                </div>

                <div>
                  <Label className="text-gray-700 font-medium mb-2 block">
                    Taux horaire brut moyen chargé (€/h)
                    <InfoTooltip text="Coût horaire moyen d'un collaborateur, charges comprises. Utilisé pour valoriser les gains de temps." />
                  </Label>
                  <Input
                    type="text"
                    value={formData.hourlyRate}
                    onChange={(e) => handleDecimalChange('hourlyRate', e.target.value)}
                    placeholder="Ex: 45"
                    className="bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Section B: Temps par tâche */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#1C2E4A' }}>
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Temps consacré aux tâches cibles</h3>
                  <p className="text-sm text-gray-500">Indiquez le nombre d'heures par mois pour chaque catégorie</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { key: 'adminTime', label: 'Saisie administrative', placeholder: 'h/mois' },
                  { key: 'customerFollowUp', label: 'Suivi client', placeholder: 'h/mois' },
                  { key: 'prospection', label: 'Prospection commerciale', placeholder: 'h/mois' },
                  { key: 'research', label: 'Veille et recherche', placeholder: 'h/mois' },
                  { key: 'writing', label: 'Rédaction / synthèse', placeholder: 'h/mois' },
                  { key: 'contentCreation', label: 'Création de supports', placeholder: 'h/mois' },
                  { key: 'otherTasks', label: 'Autres tâches répétitives', placeholder: 'h/mois (optionnel)' },
                ].map((field) => (
                  <div key={field.key}>
                    <Label className="text-gray-700 font-medium mb-2 block text-sm">
                      {field.label}
                    </Label>
                    <Input
                      type="text"
                      value={formData[field.key]}
                      onChange={(e) => handleNumberChange(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="bg-white"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Section C: Niveau d'adoption */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#1C2E4A' }}>
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Niveau d'adoption envisagé</h3>
                  <p className="text-sm text-gray-500">Sélectionnez le niveau correspondant à votre projet</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(adoptionLevels).map(([key, level]) => (
                  <div
                    key={key}
                    onClick={() => handleChange('adoptionLevel', key)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.adoptionLevel === key
                        ? 'border-[#1C2E4A] bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                        formData.adoptionLevel === key ? 'border-[#1C2E4A] bg-[#1C2E4A]' : 'border-gray-300'
                      }`}>
                        {formData.adoptionLevel === key && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-gray-900">{level.label}</h4>
                          <span className="text-sm font-medium" style={{ color: '#1C2E4A' }}>
                            {(level.gainRate * 100).toFixed(0)}% de gain
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{level.description}</p>
                        <p className="text-sm text-gray-500 mt-2">
                          Coût indicatif : {formatCurrency(level.defaultCost)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Coût personnalisé */}
              {formData.adoptionLevel && (
                <div className="mt-6 p-4 bg-white rounded-xl border border-gray-200">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium mb-2 block text-sm">
                        Coût d'implémentation personnalisé (optionnel)
                        <InfoTooltip text="Laissez vide pour utiliser le coût indicatif par défaut." />
                      </Label>
                      <Input
                        type="text"
                        value={formData.customCost}
                        onChange={(e) => handleNumberChange('customCost', e.target.value)}
                        placeholder={`Par défaut : ${formatCurrency(adoptionLevels[formData.adoptionLevel]?.defaultCost || 0)}`}
                        className="bg-gray-50"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium mb-2 block text-sm">
                        Coût de maintenance annuel (années 2-3)
                      </Label>
                      <Input
                        type="text"
                        value={formData.maintenanceCost}
                        onChange={(e) => handleNumberChange('maintenanceCost', e.target.value)}
                        placeholder="Ex: 500"
                        className="bg-gray-50"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bouton de simulation */}
            <div className="text-center">
              <Button
                onClick={handleSimulate}
                disabled={!isFormValid()}
                className="text-white font-semibold py-4 px-12 rounded-full text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#1C2E4A' }}
              >
                <Calculator className="w-5 h-5 mr-2" />
                Calculer mon ROI estimé
              </Button>
              {!isFormValid() && (
                <p className="text-sm text-gray-500 mt-3">
                  Veuillez remplir tous les champs obligatoires pour lancer la simulation.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Résultats */}
      {showResults && calculations && (
        <section id="results-section" className="py-16 px-4" style={{ backgroundColor: '#E9DFCF' }}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Résultats de votre simulation
              </h2>
              <p className="text-gray-600">
                Estimation basée sur les informations renseignées et le niveau d'adoption sélectionné.
              </p>
            </div>

            {/* Indicateurs principaux */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <ResultCard
                icon={Clock}
                label="Temps économisé estimé"
                value={`${formatNumber(calculations.annualTimeSaved, 0)} h/an`}
                subValue={`soit ${formatNumber(calculations.annualDaysSaved, 1)} jours/an`}
              />
              <ResultCard
                icon={Euro}
                label="Gain économique annuel estimé"
                value={formatCurrency(calculations.annualEconomicGain)}
                highlight
              />
              <ResultCard
                icon={PieChart}
                label="Poids économique des gains"
                value={`${formatNumber(calculations.economicWeight, 2)} %`}
                subValue="du chiffre d'affaires"
              />
            </div>

            {/* ROI et projections */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6" style={{ color: '#1C2E4A' }} />
                Retour sur investissement et projections
              </h3>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {/* Année 1 */}
                <div className={`p-5 rounded-xl border-2 ${calculations.roiYear1 >= 0 ? 'border-green-300 bg-green-50' : 'border-orange-300 bg-orange-50'}`}>
                  <p className="text-sm font-medium text-gray-600 mb-1">Année 1</p>
                  <p className={`text-2xl font-bold ${calculations.roiYear1 >= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                    {calculations.roiYear1 >= 0 ? '+' : ''}{formatNumber(calculations.roiYear1, 0)}% ROI
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Bénéfice net : {formatCurrency(calculations.netBenefitYear1)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Coût : {formatCurrency(calculations.implementationCost)}
                  </p>
                </div>

                {/* Année 2 */}
                <div className="p-5 rounded-xl border border-gray-200 bg-gray-50">
                  <p className="text-sm font-medium text-gray-600 mb-1">Année 2 (projection)</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(calculations.gainYear2)}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Bénéfice net : {formatCurrency(calculations.netBenefitYear2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    +10% vs année 1
                  </p>
                </div>

                {/* Année 3 */}
                <div className="p-5 rounded-xl border border-gray-200 bg-gray-50">
                  <p className="text-sm font-medium text-gray-600 mb-1">Année 3 (projection)</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(calculations.gainYear3)}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Bénéfice net : {formatCurrency(calculations.netBenefitYear3)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    +20% vs année 1
                  </p>
                </div>
              </div>

              {/* Cumul 3 ans */}
              <div className="p-5 rounded-xl bg-[#1C2E4A] text-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-blue-200 text-sm font-medium">Cumul sur 3 ans</p>
                    <p className="text-3xl font-bold">{formatCurrency(calculations.totalNetBenefit3Years)}</p>
                    <p className="text-blue-200 text-sm mt-1">de bénéfice net estimé</p>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-200 text-sm">Gains totaux : {formatCurrency(calculations.totalGain3Years)}</p>
                    <p className="text-blue-200 text-sm">Coûts totaux : {formatCurrency(calculations.totalCost3Years)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tableau de synthèse */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200 mb-8 overflow-x-auto">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <BarChart3 className="w-6 h-6" style={{ color: '#1C2E4A' }} />
                Tableau de synthèse
              </h3>

              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Indicateur</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Mensuel</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Annuel</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Commentaire</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 text-gray-700">Temps sur tâches ciblées</td>
                    <td className="px-4 py-3 text-right font-medium">{formatNumber(calculations.monthlyTotalHours)} h</td>
                    <td className="px-4 py-3 text-right font-medium">{formatNumber(calculations.monthlyTotalHours * 12)} h</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">Total déclaré</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 text-gray-700">Taux d'amélioration retenu</td>
                    <td className="px-4 py-3 text-right font-medium">—</td>
                    <td className="px-4 py-3 text-right font-medium">{formatNumber(calculations.gainRate)}%</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">Selon niveau d'adoption</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-green-50">
                    <td className="px-4 py-3 text-gray-700 font-medium">Gain de temps estimé</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{formatNumber(calculations.monthlyTimeSaved, 1)} h</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{formatNumber(calculations.annualTimeSaved, 0)} h</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatNumber(calculations.annualDaysSaved, 1)} jours/an</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-green-50">
                    <td className="px-4 py-3 text-gray-700 font-medium">Gain économique estimé</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{formatCurrency(calculations.annualEconomicGain / 12)}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{formatCurrency(calculations.annualEconomicGain)}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">Valorisation au taux horaire</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 text-gray-700">Poids économique (% CA)</td>
                    <td className="px-4 py-3 text-right font-medium">—</td>
                    <td className="px-4 py-3 text-right font-medium">{formatNumber(calculations.economicWeight, 2)}%</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">Part du CA valorisable</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 text-gray-700">Coût d'implémentation</td>
                    <td className="px-4 py-3 text-right font-medium">—</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(calculations.implementationCost)}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">Année 1</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 text-gray-700 font-medium">Bénéfice net année 1</td>
                    <td className="px-4 py-3 text-right font-medium">—</td>
                    <td className={`px-4 py-3 text-right font-bold ${calculations.netBenefitYear1 >= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                      {formatCurrency(calculations.netBenefitYear1)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">Gains - coûts</td>
                  </tr>
                  <tr className="bg-blue-50">
                    <td className="px-4 py-3 text-gray-700 font-semibold">ROI année 1</td>
                    <td className="px-4 py-3 text-right font-medium">—</td>
                    <td className={`px-4 py-3 text-right font-bold ${calculations.roiYear1 >= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                      {calculations.roiYear1 >= 0 ? '+' : ''}{formatNumber(calculations.roiYear1, 0)}%
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">Retour sur investissement</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tableau projection 3 ans */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200 mb-8 overflow-x-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Projection sur 3 ans</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Année</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Gain estimé</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Coût estimé</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Bénéfice net</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-700">Année 1</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(calculations.annualEconomicGain)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(calculations.implementationCost)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${calculations.netBenefitYear1 >= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                      {formatCurrency(calculations.netBenefitYear1)}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-700">Année 2</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(calculations.gainYear2)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(calculations.maintenanceCost)}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{formatCurrency(calculations.netBenefitYear2)}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-700">Année 3</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(calculations.gainYear3)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(calculations.maintenanceCost)}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{formatCurrency(calculations.netBenefitYear3)}</td>
                  </tr>
                  <tr className="bg-[#1C2E4A] text-white">
                    <td className="px-4 py-3 font-semibold">Total 3 ans</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(calculations.totalGain3Years)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(calculations.totalCost3Years)}</td>
                    <td className="px-4 py-3 text-right font-bold">{formatCurrency(calculations.totalNetBenefit3Years)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mentions de prudence */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
              <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5" />
                Avertissement et conditions d'interprétation
              </h4>
              <p className="text-sm text-amber-700 mb-3">
                Cette simulation constitue une <strong>estimation indicative</strong> réalisée à partir des informations renseignées. 
                Les résultats affichés ne constituent pas un engagement contractuel et dépendent notamment du niveau réel d'adoption, 
                de l'organisation de la structure, de la qualité du déploiement, du niveau d'accompagnement et de la maturité des équipes.
              </p>
              <p className="text-sm text-amber-700">
                Les gains estimés reposent sur des hypothèses paramétrables de productivité et doivent être interprétés comme des 
                <strong> ordres de grandeur</strong> destinés à faciliter la prise de décision.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* CTA Final */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12 border border-gray-200">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#1C2E4A' }}>
              <Target className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Obtenez une analyse adaptée à votre structure
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Une étude personnalisée permet d'affiner les hypothèses, d'identifier les cas d'usage prioritaires 
              pour votre activité et de construire un plan de déploiement réaliste et progressif.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#formulaire">
                <Button
                  className="text-white font-semibold py-4 px-8 rounded-full"
                  style={{ backgroundColor: '#1C2E4A' }}
                >
                  Demander un diagnostic personnalisé
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="font-semibold py-4 px-8 rounded-full"
                >
                  Planifier un échange
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SimulationROIPage;
